import { useState } from 'react';
import { CircularProgress, Dialog, Chip, Box, Typography, Divider, Grid, Paper, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { DialogContent } from '@mui/material';
import { IoIosClose } from 'react-icons/io';
import { ImageWithBoundingBoxes } from './ImageBboxes';

const ImageDetailsDialog = ({ dialogOpen, handleCloseDialog, selectedImage, imageLoaded, formatTimestamp, bayDetails }) => {
  const [hoveredObjectType, setHoveredObjectType] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!selectedImage) return null;

  const metadata = selectedImage.metadata[0] || {};
  const userDetails = selectedImage.userDetails || {};
  const { date, time } = formatTimestamp(metadata.timestamp);
  const detectedObjects = metadata.tray_det_bboxes || [];
  const missingDescriptions = metadata.missing_pop?.map((item) => item.vmDescription).filter(Boolean) ?? [];

  const format = bayDetails?.data[0]?.format || '';
  const isSevenEleven = format === 'SevenEleven';
  console.log('isseven eleven is ', isSevenEleven);

  let osaScore = metadata?.fullness ?? 'N/A';
  let product_score = 'N/A';
  // let vmScore = 'N/A';

  if (isSevenEleven) {
    const totalPop = metadata.total_pop || 0;
    const matched = metadata.pop_matching_result?.matched || 0;
    // const incorrect = metadata.pop_matching_result?.incorrect || 0;
    // const wronglyPlaced = metadata.pop_matching_result?.wrongly_placed || 0;

    osaScore = totalPop > 0 ? ((matched / totalPop) * 100).toFixed(2) : 'N/A';
    product_score = metadata?.product_score ?? 'N/A';

    // if (totalPop > 0) {
    //   const vmCalculated = ((totalPop - (incorrect + wronglyPlaced)) / totalPop) * 100;
    //   // vmScore = vmCalculated.toFixed(2);
    // }
  }

  // Count occurrences of each category
  const objectCounts = {};
  detectedObjects.forEach((box) => {
    const category = box.name || box.type || 'Unknown';
    objectCounts[category] = (objectCounts[category] || 0) + 1;
  });

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '80vh',
          maxHeight: isMobile ? '100%' : '700px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          m: isMobile ? 0 : 2,
          borderRadius: isMobile ? 0 : 3,
          position: 'relative',
          overflow: 'visible',
          backgroundColor: '#fff'
        }
      }}
    >
      <IconButton
        onClick={handleCloseDialog}
        sx={{
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? 8 : 16,
          right: isMobile ? 8 : 24,
          zIndex: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.05)',
            transform: 'scale(1.1)'
          },
          transition: 'all 0.2s ease',
          width: isMobile ? 36 : 40,
          height: isMobile ? 36 : 40,
          border: '1px solid rgba(0,0,0,0.1)'
        }}
      >
        <IoIosClose className={isMobile ? 'text-2xl' : 'text-3xl'} />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'visible',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          flexGrow: 1,
          '&.MuiDialogContent-root': {
            padding: 0
          }
        }}
      >
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            height: isMobile ? '45vh' : '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: isMobile ? 1 : 2,
            backgroundColor: '#f8f8f8'
          }}
        >
          <Paper
            sx={{
              p: isMobile ? 1 : 2,
              height: '100%',
              width: '100%',
              backgroundColor: '#f8f8f8',
              overflow: 'visible',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: isMobile ? 1 : 2,
              boxShadow: 'none'
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              {!imageLoaded && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    zIndex: 1
                  }}
                >
                  <CircularProgress size={isMobile ? 24 : 40} />
                </Box>
              )}
              <Box
                sx={{
                  display: imageLoaded ? 'block' : 'none',
                  width: '100%',
                  height: '100%',
                  position: 'relative'
                }}
              >
                <ImageWithBoundingBoxes imageData={selectedImage} clickable={false} hoveredObjectType={hoveredObjectType} />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <div className={`${isMobile ? 'w-full' : 'md:w-[33vw]'} h-full flex flex-col w-full p-4 bg-white overflow-y-auto`}>
          <div className="flex-grow flex flex-col space-y-4 overflow-y-auto pr-2" style={{ paddingTop: isMobile ? '10px' : '32px' }}>
            <div className="w-full flex justify-between items-center pb-2">
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                fontWeight="bold"
                color="primary"
                sx={{ fontSize: isMobile ? '1.1rem' : '1.35rem', lineHeight: 1.2 }}
              >
                {bayDetails?.data[0]?.bay_id || 'N/A'}
              </Typography>
            </div>

            <div className="space-y-2">
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="medium" color="text.secondary">
                Timestamp Details
              </Typography>
              <Divider />
              <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
                {date} at {time}
              </Typography>
            </div>

            <div className="space-y-2">
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="medium" color="text.secondary">
                SEL Compliance Score
              </Typography>
              <Divider />
              <Typography
                variant={isMobile ? 'body2' : 'body1'}
                sx={{
                  fontWeight: 'bold',
                  color:
                    osaScore !== 'N/A'
                      ? parseFloat(osaScore) < 50
                        ? '#d32f2f'
                        : parseFloat(osaScore) < 80
                        ? '#fb8c00'
                        : '#28A745'
                      : 'inherit'
                }}
              >
                {osaScore !== 'N/A' ? `${osaScore}%` : 'N/A'}
              </Typography>
            </div>
            <div className="space-y-2">
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="medium" color="text.secondary">
                Product Compliance Score
              </Typography>
              <Divider />
              <Typography
                variant={isMobile ? 'body2' : 'body1'}
                sx={{
                  fontWeight: 'bold',
                  color:
                    product_score !== 'N/A'
                      ? parseFloat(product_score) < 50
                        ? '#d32f2f'
                        : parseFloat(product_score) < 80
                        ? '#fb8c00'
                        : '#28A745'
                      : 'inherit'
                }}
              >
                {product_score !== 'N/A' ? `${product_score}%` : 'N/A'}
              </Typography>
            </div>

            {/* <div className="space-y-2">
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="medium" color="text.secondary">
                VM Score
              </Typography>
              <Divider />
              <Typography
                variant={isMobile ? 'body2' : 'body1'}
                sx={{
                  fontWeight: 'bold',
                  color: vmScore !== 'N/A' ? (parseFloat(vmScore) > 85 ? '#2e7d32' : '#d32f2f') : 'inherit'
                }}
              >
                {vmScore !== 'N/A' ? `${vmScore}%` : 'N/A'}
              </Typography>
            </div> */}

            {missingDescriptions.length > 0 && (
              <div className="space-y-3">
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                  Missing Items:
                </Typography>
                <div
                  style={{
                    maxHeight: '6.5rem',
                    overflowY: 'auto',
                    paddingRight: '8px'
                  }}
                >
                  <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                    {missingDescriptions.map((desc, index) => (
                      <li key={index} style={{ color: '#ff9800', fontSize: '0.875rem' }}>
                        {desc}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="medium" color="text.secondary">
                User Details
              </Typography>
              <Divider />
              <div className="w-full space-y-2">
                <Typography variant={isMobile ? 'body2' : 'body1'}>
                  <span style={{ fontWeight: 500, color: '#666' }}>Name:</span> {userDetails.user_name || 'N/A'}
                </Typography>
                <Typography variant={isMobile ? 'body2' : 'body1'}>
                  <span style={{ fontWeight: 500, color: '#666' }}>Number:</span> {userDetails.number || 'N/A'}
                </Typography>
              </div>
            </div>

            <div className="space-y-3">
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="medium" color="text.secondary">
                Detected Objects
              </Typography>
              <Divider />
              {detectedObjects.length > 0 ? (
                <div className="w-full flex flex-wrap gap-2">
                  {Object.keys(objectCounts)
                    .filter((category) => category !== 'filled') // Filter out 'filled' objects as per memory requirement
                    .map((category) => (
                      <Chip
                        key={category}
                        label={
                          <div className="flex items-center space-x-1">
                            <span>{category || 'Unnamed'}</span>
                            <span>({objectCounts[category]})</span>
                          </div>
                        }
                        sx={{
                          backgroundColor:
                            category === 'empty'
                              ? '#e53935' // red for empty category
                              : hoveredObjectType === category
                              ? '#81C784'
                              : '#1976d2',
                          color: 'white',
                          borderRadius: 4,
                          px: 1,
                          height: 'auto',
                          '& .MuiChip-label': {
                            py: isMobile ? 0.5 : 1,
                            fontSize: isMobile ? '0.75rem' : '0.875rem'
                          },
                          '&:hover': {
                            backgroundColor:
                              category === 'empty'
                                ? '#d32f2f' // darker red on hover
                                : hoveredObjectType === category
                                ? '#81C784'
                                : '#1565c0'
                          },
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={() => setHoveredObjectType(category)}
                        onMouseLeave={() => setHoveredObjectType(null)}
                      />
                    ))}
                </div>
              ) : (
                <Box
                  sx={{
                    p: isMobile ? 1 : 2,
                    mt: 1,
                    borderRadius: 2,
                    backgroundColor: '#f0f4f8',
                    border: '1px dashed #ccc',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ color: '#999', fontStyle: 'italic' }}>
                    No detected objects found.
                  </Typography>
                </Box>
              )}
              {metadata.allian_pop?.length > 0 && (
                <Chip
                  key="allian-pop-label"
                  label={
                    <div className="flex items-center space-x-1">
                      <span>Allian Pop</span>
                      <span>({metadata.allian_pop.length})</span>
                    </div>
                  }
                  sx={{
                    backgroundColor: '#e53935', // Changed to red to match the allian bbox color
                    color: 'white',
                    borderRadius: 4,
                    px: 1,
                    height: 'auto',
                    '& .MuiChip-label': {
                      py: isMobile ? 0.5 : 1,
                      fontSize: isMobile ? '0.75rem' : '0.875rem'
                    },
                    transition: 'all 0.3s ease'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageDetailsDialog;
